import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  lazy,
  Suspense,
} from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// date-fns

// Material
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Divider from '@material-ui/core/Divider';

// CSVLink
import { CSVLink } from 'react-csv';

// Helpers
import { formatDateAndTime } from 'helpers/date';
import { currencyFormat } from 'helpers/currencyFormat';
import { getComparator, stableSort } from 'helpers/tableSortLabels';

// Actions
import * as ReportsActions from 'store/ducks/reports/actions';
import { setOpen, setClose } from 'store/ducks/dialogs/actions';

// Types
import { ApplicationState } from 'store';
import { PaymentInterface } from 'store/ducks/reports/types';

// Components
import ContentPlaceholder from 'components/TablePlaceholder';
import { TabPanel } from 'components/TabPanel';
import {
  MainContent,
  DialogueExport,
  SearchComponent,
  XlsHeaderInterface,
  XlsRowInterface,
  ReportTitle,
} from 'components';

// Constants
import CONST_PAYMENT from 'constants/paymentsReceived';
import IMAGES from 'constants/images';
import ITEMS from 'constants/itemsByFilter';
import { REPORTS_TYPES } from 'constants/report';
import TITLES_OPTIONS from 'constants/infoModulesTitle';
import { Submissions, History } from '../../../../components';
import TablePayments from './components/TablePayments';

// Styles
import useStyles from './ReceiptsReportDatails.styles';

// Lazy Components
const Filter = lazy(() => import('components/Filter'));

interface ReceiptsReportDatailsInterface {
  setSubmissionData: (param: any) => void;
  setIsAddSubmissiion: (param: any) => void;
  setIsEditSubmissiion: (param: any) => void;
}

const ReceiptsReportDatails: React.FC<ReceiptsReportDatailsInterface> = ({
  setSubmissionData,
  setIsAddSubmissiion,
  setIsEditSubmissiion,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const csvRef = useRef(null);

  const [periodString, setPeriodString] = useState('');
  const [xlsHeader, setXlsHeader] = useState<XlsHeaderInterface[]>();
  const [xlsRows, setXlsRows] = useState<Array<XlsRowInterface[]>>([]);

  const [simplePdfData, setSimplePdfData] = useState<{
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }>({
    title: '',
    subtitle: '',
    filters: '',
    headers: [],
    rows: [],
  });

  const initialPaymentsList = useSelector(
    (state: ApplicationState) => state.reports.paymentList,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const [tableReference, setTableReference] = useState();
  const [paymentsList, setPaymentsList] = useState<PaymentInterface[]>([]);
  const [contentIsVisible, setContentIsVisible] = useState(false);
  const [tableCSV, setTableCSV] = useState('');
  const [generalNavGuide, setGeneralNavGuide] = useState(0);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setGeneralNavGuide(newValue);
  };

  const handleSetSimplePdfData = (data: any) => {
    setSimplePdfData(data);
  };

  const handleLoadData = useCallback(() => {
    setContentIsVisible(true);
    // initial date
    const MS_DAY = 86400000; // 1Day in ms
    const now = new Date();
    const yest = new Date(now.getTime() - MS_DAY);

    dispatch(
      ReportsActions.paymentReportRequest({
        startDate: new Date().setHours(0, 0, 0),
        endDate: new Date().setHours(23, 59, 59),
        reportType: REPORTS_TYPES.SUPERVISOR_PAYMENT,
      }),
    );

    dispatch(
      ReportsActions.setSelectedStartDate({
        selectedStartDate: yest,
      }),
    );
    dispatch(
      ReportsActions.setSelectedEndDate({
        selectedEndDate: now,
      }),
    );

    const tempPeriodString = `Dia: ${new Date().toLocaleDateString('pt-BR', {
      dateStyle: 'short',
    })}`;

    setPeriodString(tempPeriodString);
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(initialPaymentsList)) {
      setPaymentsList(initialPaymentsList);
    }
  }, [initialPaymentsList]);

  // unmount
  useEffect(
    () => () => {
      dispatch(
        ReportsActions.setSelectedSupervisors({ selectedSupervisors: [] }),
      );
    },
    [dispatch],
  );

  const handleApply = useCallback(
    ({ startedAt, startDate, endDate, selectedSupervisors }: any) => {
      dispatch(setClose({}));
      setContentIsVisible(true);
      const isPeriod = startDate?.getTime() < endDate?.getTime();
      let startDateValue;
      let endDateValue;
      if (isPeriod) {
        startDateValue = new Date(startDate).getTime();
        endDateValue = new Date(endDate).getTime();
      } else if (startedAt) {
        startDateValue = new Date(startedAt).setHours(0, 0, 0);
        endDateValue = new Date(startedAt).setHours(23, 59, 59);
      }
      dispatch(
        ReportsActions.paymentReportRequest({
          startDate: startDateValue,
          endDate: endDateValue,
          supervisorId:
            selectedSupervisors?.length > 0
              ? selectedSupervisors.map((e: any) => e.id).join()
              : undefined,
          reportType: REPORTS_TYPES.SUPERVISOR_PAYMENT,
        }),
      );

      const tempPeriodString = `De: ${
        isPeriod
          ? formatDateAndTime(new Date(startDate))
          : formatDateAndTime(new Date(startedAt))
      } até: ${
        isPeriod
          ? formatDateAndTime(new Date(endDate))
          : formatDateAndTime(new Date(startedAt))
      }`;

      let updatedPeriodString = tempPeriodString;

      if (selectedSupervisors?.length > 0) {
        const tempLinesString = `Supervisores: ${selectedSupervisors
          .map((e: any) => e.name)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      setPeriodString(updatedPeriodString);
    },
    [],
  );

  const totalPaid = useMemo(
    () => paymentsList.reduce((acc, { value }) => acc + value, 0) || 0,
    [paymentsList],
  );

  // generating the table csv
  useEffect(() => {
    let csv = '';
    if (Array.isArray(paymentsList) && paymentsList.length > 0) {
      let header = `Valor;Data e Hora ;Tipo ;De ;Para`;
      if (transportOperatorSelected.type === 1) {
        header += ';Operação\n';
      } else {
        header += '\n';
      }
      csv += header;
      csv += stableSort(paymentsList, getComparator('desc', 'createdAt'))
        .map(
          (payment) =>
            `${currencyFormat({ value: payment.value || 0 })};${new Date(
              payment.date,
            ).toLocaleString('pt-BR')};${
              CONST_PAYMENT.TYPES.find(({ KEY }) => KEY === payment.type)?.VALUE
            };${payment.from};${payment.to};${
              transportOperatorSelected.type === 1
                ? transportOperatorsList.find(
                    (e) => e.id === payment?.transportOperatorId,
                  )?.name + '\n' || '---'
                : '\n'
            }`,
        )
        .join('');
      setTableCSV(csv);
    }
  }, [paymentsList]);

  useEffect(() => {
    if (paymentsList.length > 0) {
      const staticXlsheader = [
        {
          header: 'Valor',
          key: 'value',
          width: 20,
        },
        { header: 'Data / Hora', key: 'createdAt', width: 20 },
        {
          header: 'Tipo',
          key: 'type',
          width: 20,
        },
        {
          header: 'De',
          key: 'from',
          width: 20,
        },
        {
          header: 'Para',
          key: 'to',
          width: 20,
        },
      ];

      if (transportOperatorSelected.type === 1) {
        staticXlsheader.push({
          header: 'Operação',
          key: 'operator',
          width: 20,
        });
      }

      setXlsHeader(staticXlsheader);

      const rows = stableSort(
        paymentsList,
        getComparator('desc', 'createdAt'),
      ).map((data) => {
        const staticXlsRows = [
          {
            key: 'value',
            value: `${currencyFormat({ value: data.value })}`,
            image: null,
          },
          {
            key: 'createdAt',
            value: `${new Date(data.date).toLocaleString('pt-BR')}`,
            image: null,
          },
          {
            key: 'type',
            value: `${
              CONST_PAYMENT.TYPES.find(({ KEY }) => KEY === data.type)?.VALUE
            }`,
            image: null,
          },
          { key: 'from', value: `${data.from}`, image: null },
          { key: 'to', value: `${data.to}`, image: null },
        ];

        if (transportOperatorSelected.type === 1) {
          staticXlsRows.push({
            key: 'operator',
            value: `${transportOperatorsList.find(
              (e) => e.id === data?.transportOperatorId,
            )?.name || '---'}`,
            image: null,
          });
        }
        return staticXlsRows;
      }) as XlsRowInterface[][];
      setXlsRows(rows);
    }
  }, [paymentsList]);

  return (
    <div className={classes.root}>
      <MainContent className={classes.paperRoot}>
        <Grid ref={(ref: any) => setTableReference(ref)}>
          <Grid
            container
            justifyContent='space-between'
            className={classes.gridHeader}>
            <Grid item>
              <ReportTitle
                titleVariant='h4'
                title='Relatórios de Arrecadação • Recebimentos'
                tooltipTitle={TITLES_OPTIONS.REPORTS.COLLECTION.RECEIPTS.VALUE}
              />
            </Grid>
          </Grid>

          <Grid item spacing={2} className={classes.gridContent}>
            <Box display='flex' alignItems='center'>
              <SearchComponent
                disabledInput={!contentIsVisible}
                data={initialPaymentsList}
                itemsByFilter={ITEMS.receipts}
                results={(data: PaymentInterface[]) => setPaymentsList(data)}
                classes={{
                  container: classes.search,
                }}
                modalFilter={() => {
                  dispatch(
                    setOpen({
                      open: true,
                      content: (
                        <Suspense fallback={<p>Carregando...</p>}>
                          <Filter
                            fields={ITEMS.receiptsRemote}
                            handleApply={handleApply}
                          />
                        </Suspense>
                      ),
                      maxWidth: 'xl',
                    }),
                  );
                }}
              />
              {/* <Grid container justifyContent='flex-end'>
              <Button
                className={classes.addButton}
                onClick={() => setIsAddSubmissiion(true)}>
                <Typography style={{ marginRight: 8, fontSize: 20 }}>
                  +
                </Typography>{' '}
                <Typography style={{ fontSize: 12 }}>Programar Novo</Typography>
              </Button>
            </Grid> */}
            </Box>
            <Grid item>
              <CSVLink filename='result.csv' data={tableCSV} ref={csvRef} />
            </Grid>

            <Tabs
              indicatorColor='primary'
              onChange={handleChange}
              style={{
                background: '#ffffff',
                marginTop: 10,
                marginBottom: 32,
              }}
              textColor='primary'
              value={generalNavGuide}>
              <Tab label='Relatório' />
              <Tab label='Envios' />
              <Tab label='Histórico' />
            </Tabs>
            <TabPanel index={0} value={generalNavGuide}>
              {!contentIsVisible && (
                <ContentPlaceholder onClick={handleLoadData} />
              )}
              {contentIsVisible && (
                <Grid>
                  <Box
                    display='flex'
                    justifyContent='flex-end'
                    alignItems='center'>
                    <Box display='flex' alignItems='center' marginBottom='16px'>
                      <Box className={classes.resume}>
                        <Box className={classes.contentImage}>
                          <img
                            src={IMAGES.REPORTS_ICONS.CREDIT_PORTFOLIO}
                            alt='credit portfolio'
                          />
                        </Box>
                        <Divider
                          className={classes.dividerVertical}
                          orientation='vertical'
                          flexItem
                        />{' '}
                        <Grid className={classes.contentTotalEntry}>
                          <Typography className={classes.subTiltle}>
                            Recolhimentos
                          </Typography>
                          <Typography className={classes.value}>
                            {paymentsList.length}
                          </Typography>
                        </Grid>
                      </Box>
                      <Box className={classes.resume}>
                        <Box className={classes.contentImage}>
                          <img
                            src={IMAGES.REPORTS_ICONS.TOTAL_ENTRY}
                            alt='total entry'
                          />
                        </Box>
                        <Divider
                          className={classes.dividerVertical}
                          orientation='vertical'
                          flexItem
                        />{' '}
                        <Grid className={classes.contentTotalEntry}>
                          <Typography className={classes.subTiltle}>
                            Total Pago
                          </Typography>
                          <Typography className={classes.value}>
                            {`R$ ${totalPaid.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                            })}`}
                          </Typography>
                        </Grid>
                      </Box>
                      <DialogueExport
                        tableInstance={tableReference}
                        csvRef={csvRef}
                        simplePdf
                        simplePdfData={simplePdfData}
                        columnsPerPage={5}
                        pdfName='relatorio_arrecadacao_recebimentos'
                        xlsTitle='relatorio_arrecadacao_recebimentos'
                        xlsHeader={xlsHeader}
                        xlsRows={xlsRows}
                        pdf
                        csv
                        xls
                      />
                    </Box>
                  </Box>
                  <TablePayments
                    periodString={periodString}
                    paymentsList={paymentsList}
                    handleSetSimplePdfData={handleSetSimplePdfData}
                  />
                </Grid>
              )}
            </TabPanel>
            <TabPanel index={1} value={generalNavGuide}>
              <Submissions
                setSubmissionData={setSubmissionData}
                setIsEditSubmissiion={setIsEditSubmissiion}
                reportType={REPORTS_TYPES.SUPERVISOR_PAYMENT}
              />
            </TabPanel>
            <TabPanel index={2} value={generalNavGuide}>
              <History
                reportType={REPORTS_TYPES.ENTRY_VALUE_SIMPLIFIED}
                secondReportType={REPORTS_TYPES.ENTRY_VALUE}
              />
            </TabPanel>
          </Grid>
        </Grid>
      </MainContent>
    </div>
  );
};

export { ReceiptsReportDatails };
export default ReceiptsReportDatails;
